import { FrontendClinicalCodes } from "@/components/clinical-coding/FrontendClinicalCodes";
import { ClinicianModeLayout } from "@/components/layouts/ClinicianLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDemoScenarios } from "@/hooks/useDemoScenarios";
import { useEPRSettings } from "@/hooks/useEPRSettings";
import type { ClinicalCodeWithOrder } from "@/types/demo";
import { log } from "@/utils/debug";
import { getEPRStyles } from "@/utils/eprStyles";
import { AlertCircle, HelpCircle, Loader2, User2 } from "lucide-react";
import { useState } from "react";

export default function Demos() {
  const [text, setText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [matchedCodes, setMatchedCodes] = useState<ClinicalCodeWithOrder[]>([]);
  const [noMatchFound, setNoMatchFound] = useState(false);

  const { findMatchingScenario, scenarios } = useDemoScenarios();

  const { data: eprType, isLoading: isLoadingEPR } = useEPRSettings();

  // Cast eprType to the correct type, defaulting to "EPIC" if undefined
  const currentEPRType = (eprType || "EPIC") as "EPIC" | "Cerner" | "CareFlow";
  const styles = getEPRStyles(currentEPRType);

  const handleProcess = async () => {
    if (!text.trim()) return;
    setIsProcessing(true);
    setNoMatchFound(false);

    try {
      // Log input text with line break information
      log.process(`Processing text against ${scenarios?.length || 0} available scenarios`, {
        textLength: text.length,
        lineCount: text.split("\n").length,
        hasLineBreaks: text.includes("\n"),
        firstFewChars: text.substring(0, 30).replace(/\n/g, "\\n"),
      });

      if (scenarios?.length) {
        // Debug: Show basic info about available scenarios
        scenarios.forEach((scenario, index) => {
          log.process(`Scenario ${index + 1} (ID: ${scenario.id})`, {
            textLength: scenario.demo_text?.length || 0,
            lineCount: (scenario.demo_text?.match(/\n/g) || []).length + 1,
            codesCount: (scenario.clinical_codes as any[]).length,
          });
        });
      }

      const match = await findMatchingScenario(text);
      if (match) {
        // Ensure we're correctly passing the clinical codes with their order intact
        const codesWithOrder = match.clinical_codes as unknown as ClinicalCodeWithOrder[];
        setMatchedCodes(codesWithOrder);
        log.process("Found matching scenario with codes:", codesWithOrder);
      } else {
        setMatchedCodes([]);
        setNoMatchFound(true);
      }
    } catch (error) {
      console.error("Error processing demo:", error);
      setNoMatchFound(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Get header background color based on EPR type
  const getHeaderBg = () => {
    if (currentEPRType === "EPIC") return "bg-[#7FDBCA]";
    if (currentEPRType === "CareFlow") return "bg-[#f1f3f2]";
    return "bg-gradient-to-r from-[#5FA9E8] to-[#2563EB]";
  };

  // Get main background color based on EPR type
  const getMainBg = () => {
    if (currentEPRType === "EPIC") return "bg-[#E0F5F1]";
    if (currentEPRType === "CareFlow") return "bg-gray-50";
    return "bg-[#F1F1F1]";
  };

  // Get system name based on EPR type
  const getSystemName = () => {
    if (currentEPRType === "EPIC") return "EPIC";
    if (currentEPRType === "CareFlow") return "CareFlow";
    return "Cerner Millennium";
  };

  // Get text color for system name
  const getSystemNameColor = () => {
    if (currentEPRType === "EPIC") return "text-red-600";
    if (currentEPRType === "CareFlow") return "text-black";
    return "text-white";
  };

  // Get text color for header content
  const getHeaderTextColor = () => {
    if (currentEPRType === "EPIC") return "";
    if (currentEPRType === "CareFlow") return "text-black";
    return "text-white";
  };

  // Get button text color based on EPR type
  const getButtonTextColor = () => {
    if (currentEPRType === "CareFlow") return "text-black";
    return "text-white";
  };

  if (isLoadingEPR) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${getMainBg()}`}>
      {/* Header Bar */}
      <div className={`${getHeaderBg()} text-black p-2`}>
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center space-x-6">
            <div className={`font-bold text-xl ${getSystemNameColor()}`}>{getSystemName()}</div>
            <div className="flex items-center space-x-2">
              <User2
                className={`h-8 w-8 ${
                  currentEPRType === "CareFlow" ? "text-black" : currentEPRType === "EPIC" ? "text-black" : "text-white"
                }`}
              />
              <div>
                <div className={`font-semibold text-lg ${getHeaderTextColor()}`}>TESTJACKSON, HEART TRAINING</div>
                <div className={`text-sm ${getHeaderTextColor()}`}>
                  <span className="mr-4">DOB: 12/23/62</span>
                  <span className="mr-4">Age: 54 years</span>
                  <span>MRN: 64003108</span>
                </div>
              </div>
            </div>
            <div className={`text-sm ${getHeaderTextColor()}`}>
              <div>Allergies: Egg, Chocolate, penicillin</div>
              <div>Inpatient FIN: 1200291389</div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`text-sm text-right ${getHeaderTextColor()}`}>
              <div>Attending: GuynesTEST, MD, Richard D.</div>
              <div>Loc: 2S Telemetry: 2507: 0</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="m-4 bg-white min-h-[calc(100vh-6rem)] p-4">
        <div className="container mx-auto py-6 space-y-6">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-5 w-5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Enter medical text to find matching scenarios and generate clinical codes.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 flex flex-col">
              <ClinicianModeLayout
                text={text}
                onTextChange={(newText) => {
                  setText(newText);
                  log.process("Text updated in clinician mode", {
                    length: newText.length,
                    lineCount: newText.split("\n").length,
                    hasLineBreaks: newText.includes("\n"),
                  });
                }}
                eprType={currentEPRType}
              />

              {noMatchFound && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>No match found</AlertTitle>
                  <AlertDescription>
                    No matching scenario was found for the entered text. Please try with different text or create a new
                    demo scenario.
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-4">
                <Button
                  onClick={handleProcess}
                  disabled={isProcessing || !text.trim()}
                  className={`${styles.button} ${getButtonTextColor()} ml-auto`}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Process Text"
                  )}
                </Button>
              </div>
            </div>

            <div className="w-full lg:w-80">
              <FrontendClinicalCodes codes={matchedCodes} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
